import { useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ImageIcon, ChevronLeft, ChevronRight, ZoomIn, Send, Loader2, ShieldCheck, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ExecutionIcon } from "@/components/execution-icons";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { IUBENDA_PRIVACY_URL } from "@/lib/iubenda";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Galleria fotografica (placeholder finché non arrivano le foto) ---------------- */
export const ProductGallery = ({ images = [], label = "Foto in arrivo", testid = "product-gallery" }) => {
  const slides = images.length ? images : [null, null, null, null];
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const go = useCallback((n) => setIdx((i) => (n + slides.length) % slides.length), [slides.length]);

  const Slide = ({ big }) =>
    slides[idx] ? (
      <img src={slides[idx]} alt={`${label} ${idx + 1}`} className="h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-navy/25">
        <ImageIcon size={big ? 56 : 34} />
        <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
        <span className="text-[10px] text-navy/30">{idx + 1} / {slides.length}</span>
      </div>
    );

  return (
    <div
      className="select-none"
      data-testid={testid}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") go(idx - 1);
        if (e.key === "ArrowRight") go(idx + 1);
      }}
      role="group"
      aria-label="Galleria fotografica del prodotto"
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-[#E7EDF2] bg-cloud">
        <Slide />
        <button
          type="button"
          onClick={() => setZoom(true)}
          aria-label="Ingrandisci fotografia"
          data-testid="gallery-zoom"
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-navy shadow-sm transition hover:bg-white"
        >
          <ZoomIn size={16} />
        </button>
        {slides.length > 1 && (
          <>
            <button type="button" onClick={() => go(idx - 1)} aria-label="Foto precedente" data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy shadow-sm transition hover:bg-white">
              <ChevronLeft size={18} />
            </button>
            <button type="button" onClick={() => go(idx + 1)} aria-label="Foto successiva" data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy shadow-sm transition hover:bg-white">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5" aria-hidden="true">
              {slides.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-green" : "w-1.5 bg-navy/25"}`} />
              ))}
            </div>
          </>
        )}
      </div>

      {slides.length > 1 && (
        <div className="mt-3 flex gap-2.5 overflow-x-auto pb-1" role="tablist" aria-label="Miniature">
          {slides.map((s, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === idx}
              aria-label={`Vai alla foto ${i + 1}`}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setIdx(i)}
              className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border transition ${i === idx ? "border-green ring-2 ring-green/30" : "border-[#E7EDF2] hover:border-green/50"}`}
            >
              {s ? <img src={s} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center bg-cloud text-navy/20"><ImageIcon size={16} /></span>}
            </button>
          ))}
        </div>
      )}

      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="sr-only">Fotografia ingrandita</DialogTitle></DialogHeader>
          <div className="aspect-square w-full overflow-hidden rounded-xl bg-cloud">
            <Slide big />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------- Selettore colori ---------------- */
export const ColorSelector = ({ colors = [], note, value, onChange }) => {
  if (!colors.length) {
    return (
      <div data-testid="color-selector-empty">
        <div className="text-xs font-semibold uppercase tracking-wider text-navy/60">Colori</div>
        <p className="mt-1.5 text-sm text-muted-foreground">{note || "Colori disponibili in fase di conferma."}</p>
      </div>
    );
  }
  const selected = colors.find((c) => c.id === value) || colors[0];
  return (
    <div data-testid="color-selector">
      <div className="text-sm text-navy/80">
        Colore selezionato: <span className="font-semibold text-navy" data-testid="color-selected-name">{selected.name}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2.5">
        {colors.map((c) => {
          const on = c.id === selected.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onChange?.(c.id)}
              title={c.name}
              aria-label={`Colore ${c.name}`}
              aria-pressed={on}
              data-testid={`color-swatch-${c.id}`}
              className={`relative h-10 w-10 rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 ${on ? "ring-2 ring-green ring-offset-2 border-green" : "border-navy/15 hover:scale-110"}`}
              style={{ backgroundColor: c.hex }}
            >
              <span className="sr-only">{c.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- Selettore esecuzioni ---------------- */
export const ExecutionSelector = ({ executions = [], note, value, onChange }) => (
  <div data-testid="execution-selector">
    <div className="text-xs font-semibold uppercase tracking-wider text-navy/60">Esecuzioni disponibili</div>
    {executions.length ? (
      <div className="mt-3 flex flex-wrap gap-2.5">
        {executions.map((ex) => {
          const on = ex.id === value;
          return (
            <button
              key={ex.id}
              type="button"
              onClick={() => onChange?.(ex.id)}
              aria-pressed={on}
              aria-label={ex.label}
              title={ex.label}
              data-testid={`execution-${ex.id}`}
              className={`flex min-w-[84px] flex-col items-center gap-1.5 rounded-xl border px-3 py-3 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green ${on ? "border-green bg-green/8 text-green-deep" : "border-[#E7EDF2] text-navy/70 hover:border-green/50"}`}
            >
              <span className={`flex h-9 w-8 items-center justify-center ${on ? "text-green" : "text-navy/45"}`}><ExecutionIcon type={ex.icon || ex.id} size={26} mirror={ex.mirror} /></span>
              {ex.label}
            </button>
          );
        })}
      </div>
    ) : (
      <p className="mt-1.5 text-sm text-muted-foreground">{note || "Configurazioni in fase di conferma."}</p>
    )}
  </div>
);

/* ---------------- Selettore opzioni generico (classi, taglie, lato, confezione) ---------------- */
export const OptionSelector = ({ label, options = [], value, onChange, testid = "option" }) => {
  if (!options.length) return null;
  return (
    <div data-testid={`${testid}-selector`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-navy/60">{label}</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((o) => {
          const id = typeof o === "string" ? o : o.id;
          const text = typeof o === "string" ? o : o.label;
          const on = id === value;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange?.(id)}
              aria-pressed={on}
              data-testid={`${testid}-${String(id).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green ${on ? "border-green bg-green text-white shadow-sm" : "border-[#E7EDF2] text-navy/70 hover:border-green"}`}
            >
              {on && <Check size={14} strokeWidth={3} />}
              {text}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- Guida alle classi di compressione (accordion item) ---------------- */
const LEG_CLASSES = [
  ["Classe 1 — compressione leggera", "18–21 mmHg"],
  ["Classe 2 — compressione media", "23–32 mmHg"],
  ["Classe 3 — compressione forte", "34–46 mmHg"],
  ["Classe 4 — compressione molto forte", "almeno 49 mmHg"],
];
const ARM_CLASSES = [
  ["Classe 1 — compressione leggera", "15–21 mmHg"],
  ["Classe 2 — compressione media", "23–32 mmHg"],
  ["Classe 3 — compressione forte", "34–46 mmHg"],
];
const GuideTable = ({ title, rows }) => (
  <div className="mt-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-navy/60">{title}</div>
    <dl className="mt-2 overflow-hidden rounded-xl border border-[#E7EDF2]">
      {rows.map((r, i) => (
        <div key={r[0]} className={`flex justify-between gap-4 px-4 py-2.5 ${i % 2 === 0 ? "bg-cloud" : "bg-white"}`}>
          <dt className="text-sm text-navy/80">{r[0]}</dt>
          <dd className="text-sm font-semibold text-navy whitespace-nowrap">{r[1]}</dd>
        </div>
      ))}
    </dl>
  </div>
);
export const CompressionGuide = () => (
  <AccordionItem value="guida-classi">
    <AccordionTrigger data-testid="panel-guida-classi" className="text-navy">Come leggere le classi di compressione</AccordionTrigger>
    <AccordionContent>
      <p className="text-sm text-muted-foreground leading-relaxed">
        La classe di compressione indica l'intervallo di pressione esercitato dal dispositivo, espresso in millimetri di mercurio (mmHg). A un numero più alto corrisponde una compressione maggiore. Una classe superiore non è automaticamente migliore: la scelta deve essere effettuata dal medico o dal professionista sanitario in base alle esigenze individuali.
      </p>
      <GuideTable title="Calze compressive per la gamba" rows={LEG_CLASSES} />
      <GuideTable title="Dispositivi per braccio e mano" rows={ARM_CLASSES} />
      <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-amber-600" />
        La classe di compressione non deve essere scelta autonomamente. La prescrizione, la corretta misurazione e la verifica della vestibilità sono fondamentali per la sicurezza e l'efficacia del trattamento.
      </p>
    </AccordionContent>
  </AccordionItem>
);

/* ---------------- Selettore classe di compressione (desktop + mobile) ---------------- */
const CLASS_META = {
  leg: { 1: { i: "Compressione leggera", p: "18–21 mmHg" }, 2: { i: "Compressione media", p: "23–32 mmHg" }, 3: { i: "Compressione forte", p: "34–46 mmHg" }, 4: { i: "Compressione molto forte", p: "almeno 49 mmHg" } },
  arm: { 1: { i: "Compressione leggera", p: "15–21 mmHg" }, 2: { i: "Compressione media", p: "23–32 mmHg" }, 3: { i: "Compressione forte", p: "34–46 mmHg" } },
};
const _MatTable = ({ rows }) => (
  <dl className="overflow-hidden rounded-xl border border-[#E7EDF2]">
    {rows.map((m, i) => (
      <div key={m[0]} className={`flex justify-between gap-4 px-4 py-2.5 ${i % 2 === 0 ? "bg-cloud" : "bg-white"}`}>
        <dt className="text-sm text-navy/60">{m[0]}</dt><dd className="text-sm font-medium text-navy text-right">{m[1]}</dd>
      </div>
    ))}
  </dl>
);
const _Bullets = ({ items }) => (
  <ul className="space-y-2">{items.map((v) => (<li key={v} className="flex items-start gap-2.5 text-sm text-navy/80"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green" />{v}</li>))}</ul>
);
const CLASS_WARNING = "La classe di compressione non deve essere scelta autonomamente. La prescrizione, la corretta misurazione e la verifica della vestibilità sono fondamentali per la sicurezza e l'efficacia del trattamento.";

const num = (id) => Number(String(id).replace(/\D/g, ""));

export const CompressionClassSelector = ({ product, value, onChange }) => {
  const classes = product.classes || [];
  if (!classes.length) return null;
  const meta = product.slug === "handteile" ? CLASS_META.arm : CLASS_META.leg;
  const active = classes.find((c) => c.id === value)?.id || classes[0].id;
  const activeClass = classes.find((c) => c.id === active);

  const Body = ({ cls }) => {
    const n = num(cls.id);
    const m = meta[n] || {};
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {m.i && <span className="inline-flex items-center rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-navy">{m.i}</span>}
          {(m.p || product.pressure) && <span className="inline-flex items-center rounded-full bg-green px-3 py-1 text-xs font-bold text-white" data-testid="class-pressure">{m.p || product.pressure}</span>}
        </div>
        {cls.material && <div><div className="text-xs font-semibold uppercase tracking-wider text-navy/60">Composizione</div><div className="mt-2"><_MatTable rows={cls.material} /></div></div>}
        {cls.indications?.length > 0 && <div><div className="text-xs font-semibold uppercase tracking-wider text-navy/60">Indicazioni</div><div className="mt-2"><_Bullets items={cls.indications} /></div></div>}
        {product.variants?.length > 0 && <div><div className="text-xs font-semibold uppercase tracking-wider text-navy/60">Varianti disponibili</div><div className="mt-2"><_Bullets items={product.variants} /></div></div>}
      </div>
    );
  };

  return (
    <div data-testid="class-selector">
      <p className="text-sm text-muted-foreground">Seleziona una classe per visualizzare pressione, composizione, indicazioni e varianti disponibili.</p>

      {/* Desktop */}
      <div className="mt-4 hidden md:block">
        <div className="text-xs font-semibold uppercase tracking-wider text-navy/60">Scegli la classe di compressione</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {classes.map((c) => {
            const on = c.id === active;
            return (
              <button key={c.id} type="button" onClick={() => onChange(c.id)} aria-pressed={on} data-testid={`classe-${c.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-5 py-2.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green ${on ? "border-green bg-green text-white shadow-sm" : "border-[#E7EDF2] text-navy/70 hover:border-green"}`}>
                {on && <Check size={14} strokeWidth={3} />} Classe {num(c.id)}
              </button>
            );
          })}
        </div>
        <p className="sr-only" aria-live="polite" data-testid="class-live-region">Classe selezionata: Classe {num(active)}. {meta[num(active)]?.p || product.pressure || ""}</p>
        <motion.div key={active} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}
          className="mt-3 rounded-2xl border border-[#E7EDF2] border-t-4 border-t-green p-5 lg:p-6" data-testid="class-info-card">
          <h3 className="font-serif text-lg font-bold text-navy" data-testid="class-selected-label">Classe selezionata: Classe {num(active)}</h3>
          <div className="mt-4"><Body cls={activeClass} /></div>
        </motion.div>
      </div>

      {/* Mobile — fisarmonica */}
      <div className="mt-4 md:hidden">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-navy/60">Scegli la classe di compressione</div>
        <Accordion type="single" collapsible value={active} onValueChange={(v) => v && onChange(v)} className="rounded-xl border border-[#E7EDF2] px-2">
          {classes.map((c) => (
            <AccordionItem key={c.id} value={c.id} data-testid={`class-acc-${c.id}`}>
              <AccordionTrigger className="text-navy">Classe {num(c.id)} — {meta[num(c.id)]?.i}</AccordionTrigger>
              <AccordionContent><Body cls={c} /></AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="class-warning">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-amber-600" /> {CLASS_WARNING}
      </p>
    </div>
  );
};

/* ---------------- Modale "Richiedi informazioni" ---------------- */
const initial = { nome_cognome: "", telefono: "", email: "", messaggio: "", privacy: false, website: "" };

export const RequestInfoModal = ({ open, onOpenChange, summary = {}, fields }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const rows = (
    fields || [
      ["Brand", summary.brand],
      ["Prodotto", summary.product],
      ["Colore", summary.color],
      ["Esecuzione", summary.execution],
      ["Classe di compressione", summary.classe],
      ["Taglia", summary.taglia],
      ["Lato", summary.lato],
      ["Confezione", summary.confezione],
    ]
  ).filter((r) => r[1]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome_cognome || !form.telefono || !form.email) {
      toast.error("Inserisci nome, telefono ed email per inviare la richiesta.");
      return;
    }
    if (!form.privacy) {
      toast.error("Devi accettare la Privacy Policy per inviare la richiesta.");
      return;
    }
    const summaryText = rows.map((r) => `${r[0]}: ${r[1]}`).join("\n");
    const messaggio = `Richiesta informazioni prodotto\n${summaryText}${form.messaggio ? `\n\nNote: ${form.messaggio}` : ""}`;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        nome_cognome: form.nome_cognome,
        telefono: form.telefono,
        email: form.email,
        motivo: "Informazioni prodotti",
        messaggio,
        privacy_accettata: form.privacy,
        origine: summary.product ? `Richiesta informazioni prodotto — ${summary.product}` : "Richiesta informazioni prodotto",
        pagina: window.location.pathname,
        website: form.website,
      });
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm(initial);
      onOpenChange(false);
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova o chiamaci direttamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="request-info-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-navy">Richiedi informazioni</DialogTitle>
        </DialogHeader>

        {rows.length > 0 && (
          <div className="rounded-xl border border-green/20 bg-green/5 p-4" data-testid="request-summary">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-green-deep">Riepilogo della richiesta</div>
            <dl className="mt-2 space-y-1">
              {rows.map((r) => (
                <div key={r[0]} className="flex justify-between gap-4 text-sm">
                  <dt className="text-navy/60">{r[0]}</dt>
                  <dd className="text-right font-medium text-navy">{r[1]}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="request-info-form">
          <div>
            <Label htmlFor="ri-nome" className="text-navy">Nome e Cognome</Label>
            <Input id="ri-nome" data-testid="ri-input-nome" value={form.nome_cognome} onChange={set("nome_cognome")} placeholder="Mario Rossi" className="mt-1.5 bg-white h-11" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ri-tel" className="text-navy">Telefono</Label>
              <Input id="ri-tel" data-testid="ri-input-telefono" value={form.telefono} onChange={set("telefono")} placeholder="339 1424138" className="mt-1.5 bg-white h-11" />
            </div>
            <div>
              <Label htmlFor="ri-email" className="text-navy">Email</Label>
              <Input id="ri-email" type="email" data-testid="ri-input-email" value={form.email} onChange={set("email")} placeholder="nome@email.it" className="mt-1.5 bg-white h-11" />
            </div>
          </div>
          <div>
            <Label htmlFor="ri-msg" className="text-navy">Note (facoltativo)</Label>
            <Textarea id="ri-msg" data-testid="ri-input-messaggio" value={form.messaggio} onChange={set("messaggio")} placeholder="Aggiungi eventuali dettagli utili…" rows={3} className="mt-1.5 bg-white resize-none" />
          </div>
          {/* Honeypot anti-spam */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} aria-hidden="true" className="hidden" />
          <p className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed" data-testid="request-health-notice">
            <ShieldCheck size={15} className="mt-0.5 shrink-0 text-green" />
            Non inserire diagnosi, patologie, prescrizioni, referti o altre informazioni relative alla salute. La richiesta non costituisce un ordine di acquisto.
          </p>
          <div className="flex items-start gap-3">
            <Checkbox
              id="ri-privacy"
              checked={form.privacy}
              onCheckedChange={(v) => setForm((f) => ({ ...f, privacy: v === true }))}
              data-testid="ri-privacy-checkbox"
              className="mt-0.5 border-navy/30 data-[state=checked]:bg-green data-[state=checked]:border-green"
            />
            <Label htmlFor="ri-privacy" className="text-sm font-normal leading-relaxed text-navy/80 cursor-pointer">
              Dichiaro di aver letto l&apos;{" "}
              <a href={IUBENDA_PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-green underline underline-offset-2" data-testid="ri-privacy-link">Informativa Privacy</a>.
            </Label>
          </div>
          <button type="submit" data-testid="ri-submit-button" disabled={loading || !form.privacy} className="btn-green w-full disabled:opacity-60 disabled:cursor-not-allowed">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Invio in corso…</> : <>Invia richiesta <Send size={16} /></>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
